/**
 * Legal HR professional SaaS gestionale layout.
 * Fixed left sidebar (256px) + main content area. Scoped CSS via `.hr-app` class
 * so this never affects the public marketing site.
 */
import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    Building2, Calendar, FileDown, Settings, LogOut, Scale,
    Search, Command, ChevronRight, LayoutGrid,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { hrListClients } from "../lib/api";

const NAV_ITEMS = [
    { to: "/admin/hr", label: "Portfolio Clienti", icon: Building2, exact: true },
    { to: "/admin/hr/calendario", label: "Calendario Scadenze", icon: Calendar },
    { to: "/admin/hr/report", label: "Report & Export", icon: FileDown },
    { to: "/admin/hr/impostazioni", label: "Impostazioni", icon: Settings, placeholder: true },
];

export default function HRLayout({ children, title, subtitle, breadcrumbs = [], actions }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [cmdOpen, setCmdOpen] = useState(false);

    // Keyboard shortcut Cmd/Ctrl+K
    useEffect(() => {
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setCmdOpen((v) => !v);
            }
            if (e.key === "Escape") setCmdOpen(false);
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []);

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    const isActive = (item) => {
        if (item.exact) return location.pathname === item.to;
        return location.pathname.startsWith(item.to);
    };

    return (
        <div className="hr-app min-h-screen bg-[#F8FAFC] text-slate-900 antialiased" data-testid="hr-layout">
            {/* Sidebar */}
            <aside className="fixed left-0 top-0 bottom-0 z-30 w-64 bg-[#0F172A] text-slate-300 border-r border-slate-800 flex flex-col select-none" data-testid="hr-sidebar">
                {/* Logo */}
                <div className="h-16 px-5 border-b border-slate-800/80 flex items-center justify-between">
                    <Link to="/admin/hr" className="flex items-center gap-2.5 text-white font-bold tracking-tight hover:text-blue-300 transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-md">
                            <Scale size={16} className="text-white" />
                        </div>
                        <div className="leading-tight">
                            <div className="text-sm">Legal HR</div>
                            <div className="text-[9px] text-slate-500 uppercase tracking-widest font-medium">Studio Candido</div>
                        </div>
                    </Link>
                </div>

                {/* Navigation */}
                <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
                    <div className="px-3 py-2 text-[9px] uppercase tracking-widest font-bold text-slate-500">Area di lavoro</div>
                    {NAV_ITEMS.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item);
                        return (
                            <Link
                                key={item.to}
                                to={item.placeholder ? "#" : item.to}
                                onClick={item.placeholder ? (e) => e.preventDefault() : undefined}
                                data-testid={`nav-${item.to.split("/").pop()}`}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-semibold transition-all ${
                                    active
                                        ? "bg-blue-600 text-white shadow-sm"
                                        : item.placeholder
                                        ? "text-slate-500 cursor-not-allowed"
                                        : "text-slate-400 hover:text-white hover:bg-slate-800/80"
                                }`}
                                title={item.placeholder ? "Prossimamente" : item.label}
                            >
                                <Icon size={15} className="flex-shrink-0" />
                                <span className="flex-1">{item.label}</span>
                                {item.placeholder && (
                                    <span className="text-[8px] uppercase font-bold bg-slate-700/60 text-slate-400 px-1.5 py-0.5 rounded">
                                        soon
                                    </span>
                                )}
                            </Link>
                        );
                    })}

                    <div className="pt-4 px-3 pb-2 text-[9px] uppercase tracking-widest font-bold text-slate-500">Scorciatoie</div>
                    <button
                        onClick={() => setCmdOpen(true)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-semibold text-slate-400 hover:text-white hover:bg-slate-800/80 transition-all"
                        data-testid="nav-cmdk"
                    >
                        <Search size={15} className="flex-shrink-0" />
                        <span className="flex-1 text-left">Ricerca rapida</span>
                        <kbd className="hidden xl:inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-slate-800 text-slate-500 border border-slate-700 rounded text-[9px] font-mono">
                            <Command size={9} />K
                        </kbd>
                    </button>
                    <a
                        href="/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-semibold text-slate-400 hover:text-white hover:bg-slate-800/80 transition-all"
                    >
                        <LayoutGrid size={15} className="flex-shrink-0" />
                        <span className="flex-1">Vai al sito pubblico</span>
                    </a>
                </nav>

                {/* User footer */}
                <div className="p-3 border-t border-slate-800/80">
                    <div className="p-3 bg-slate-800/60 rounded-xl border border-slate-700/50 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {(user?.email || "A").charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[11px] font-semibold text-white truncate">Admin</div>
                            <div className="text-[10px] text-slate-500 truncate font-mono">{user?.email}</div>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-md transition-colors"
                            data-testid="btn-logout"
                            title="Esci"
                        >
                            <LogOut size={14} />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main content area */}
            <div className="pl-64 min-h-screen flex flex-col">
                {/* Top bar */}
                <header className="h-14 px-8 border-b border-slate-200 bg-white/90 backdrop-blur-md sticky top-0 z-20 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs">
                        <Link to="/admin/hr" className="text-slate-500 hover:text-slate-900 font-medium transition-colors">
                            Legal HR
                        </Link>
                        {breadcrumbs.map((b, i) => (
                            <React.Fragment key={i}>
                                <ChevronRight size={12} className="text-slate-300" />
                                {b.to ? (
                                    <Link to={b.to} className="text-slate-500 hover:text-slate-900 font-medium transition-colors">{b.label}</Link>
                                ) : (
                                    <span className="text-slate-900 font-semibold">{b.label}</span>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setCmdOpen(true)}
                            className="hidden md:inline-flex items-center gap-2 h-8 px-3 bg-white border border-slate-200 rounded-lg text-xs text-slate-500 hover:border-slate-400 transition-colors"
                            data-testid="topbar-cmdk"
                        >
                            <Search size={13} />
                            <span>Cerca...</span>
                            <kbd className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-slate-100 text-slate-500 border border-slate-200 rounded text-[9px] font-mono ml-2">
                                <Command size={8} />K
                            </kbd>
                        </button>
                    </div>
                </header>

                {/* Page header + content */}
                <main className="flex-1 px-8 py-7">
                    {(title || actions) && (
                        <div className="max-w-[1400px] mx-auto mb-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
                            <div>
                                {title && <h1 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h1>}
                                {subtitle && <p className="mt-1 text-sm text-slate-500 max-w-2xl">{subtitle}</p>}
                            </div>
                            {actions && <div className="flex items-center gap-2">{actions}</div>}
                        </div>
                    )}
                    <div className="max-w-[1400px] mx-auto space-y-6">
                        {children}
                    </div>
                </main>
            </div>

            {/* Command palette modal */}
            {cmdOpen && <CommandPalette onClose={() => setCmdOpen(false)} />}
        </div>
    );
}

// ============== Command Palette ==============
function CommandPalette({ onClose }) {
    const [query, setQuery] = useState("");
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        hrListClients().then((cs) => { setClients(cs); setLoading(false); }).catch(() => setLoading(false));
    }, []);

    const filtered = clients.filter((c) =>
        !query || c.ragione_sociale.toLowerCase().includes(query.toLowerCase()) || (c.settore || "").toLowerCase().includes(query.toLowerCase())
    );

    const quickActions = [
        { label: "Vai al Portfolio", to: "/admin/hr", icon: Building2 },
        { label: "Vai al sito pubblico", to: "/", icon: LayoutGrid, external: true },
    ].filter(a => !query || a.label.toLowerCase().includes(query.toLowerCase()));

    const handleSelect = (to, external) => {
        onClose();
        if (external) window.open(to, "_blank");
        else navigate(to);
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center pt-24"
            onClick={onClose}
            data-testid="cmd-palette"
        >
            <div
                className="w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center gap-3 px-4 border-b border-slate-200">
                    <Search size={16} className="text-slate-400 flex-shrink-0" />
                    <input
                        autoFocus
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Cerca clienti, azioni..."
                        className="flex-1 h-12 bg-transparent border-0 outline-0 text-sm text-slate-900 placeholder:text-slate-400"
                        data-testid="cmd-input"
                    />
                    <kbd className="text-[10px] bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded font-mono text-slate-500">ESC</kbd>
                </div>
                <div className="max-h-96 overflow-y-auto p-2">
                    {loading && <div className="px-3 py-4 text-xs text-slate-500 text-center">Caricamento...</div>}
                    {quickActions.length > 0 && (
                        <>
                            <div className="px-3 pt-2 pb-1 text-[9px] uppercase tracking-widest font-bold text-slate-400">Azioni</div>
                            {quickActions.map((a, i) => (
                                <button
                                    key={i}
                                    onClick={() => handleSelect(a.to, a.external)}
                                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-100 text-left transition-colors"
                                >
                                    <a.icon size={14} className="text-slate-500" />
                                    <span className="text-xs text-slate-700 font-medium">{a.label}</span>
                                </button>
                            ))}
                        </>
                    )}
                    {filtered.length > 0 && (
                        <>
                            <div className="px-3 pt-3 pb-1 text-[9px] uppercase tracking-widest font-bold text-slate-400">Clienti</div>
                            {filtered.slice(0, 8).map((c) => (
                                <button
                                    key={c.id}
                                    onClick={() => handleSelect(`/admin/hr/clients/${c.id}`)}
                                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-100 text-left transition-colors"
                                >
                                    <Building2 size={14} className="text-slate-500" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs text-slate-900 font-semibold truncate">{c.ragione_sociale}</div>
                                        <div className="text-[10px] text-slate-500 truncate">{c.settore || "—"} · Score {c.health_score}</div>
                                    </div>
                                    <ChevronRight size={13} className="text-slate-300" />
                                </button>
                            ))}
                        </>
                    )}
                    {!loading && filtered.length === 0 && quickActions.length === 0 && (
                        <div className="px-3 py-6 text-xs text-slate-500 text-center">Nessun risultato per "{query}"</div>
                    )}
                </div>
            </div>
        </div>
    );
}
